import { useState } from 'react';
import { categories as Api, type Category } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { Shim, SkeletonRows } from '../components/Shimmer';
import { Modal } from '../components/Modal';

/**
 * The vocabulary the article editor and the site's filter chips both read.
 *
 * Two operations here touch published pages, so both say what they did rather
 * than just succeeding: a rename reports how many articles moved with it, and
 * a delete refuses while anything is still filed under it unless a destination
 * is chosen.
 */
export function Categories() {
  const { data, error, loading, reload } = useAsync((signal) => Api.list(signal));
  const [adding, setAdding] = useState('');
  const [editing, setEditing] = useState<Category | null>(null);
  const [draft, setDraft] = useState('');
  const [removing, setRemoving] = useState<Category | null>(null);
  const [moveTo, setMoveTo] = useState('');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);

  const rows = data?.categories ?? [];

  async function act(what: () => Promise<string>) {
    setFormError('');
    setBusy(true);
    try {
      setNote(await what());
      await reload();
    } catch (e) {
      setFormError((e as Error).message);
    } finally { setBusy(false); }
  }

  const add = () => act(async () => {
    const name = adding.trim();
    if (!name) throw new Error('Give the category a name.');
    await Api.create(name);
    setAdding('');
    return `Added ${name}.`;
  });

  const save = () => act(async () => {
    if (!editing) return '';
    const res = await Api.rename(editing.id, draft.trim());
    const moved = res.category.moved;
    setEditing(null);
    return moved
      ? `Renamed to ${res.category.name} — ${moved} article${moved === 1 ? '' : 's'} moved with it.`
      : `Renamed to ${res.category.name}.`;
  });

  const drop = () => act(async () => {
    if (!removing) return '';
    const res = await Api.remove(removing.id, moveTo);
    const name = removing.name;
    setRemoving(null);
    setMoveTo('');
    return res.moved
      ? `Deleted ${name} — ${res.moved} article${res.moved === 1 ? '' : 's'} moved to ${res.to}.`
      : `Deleted ${name}.`;
  });

  const move = (c: Category, by: -1 | 1) => act(async () => {
    const order = rows.map((r) => r.id);
    const i = order.indexOf(c.id);
    const j = i + by;
    if (i < 0 || j < 0 || j >= order.length) return '';
    [order[i], order[j]] = [order[j]!, order[i]!];
    await Api.reorder(order);
    return '';
  });

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Categories</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            {loading && !data ? <Shim w={210} h={12} style={{ marginTop: 3 }} />
              : `${rows.length} categor${rows.length === 1 ? 'y' : 'ies'} — the article editor’s dropdown, `
                + 'and the filter chips on the articles page, in this order'}
          </p>
        </div>
      </div>

      {error ? <div className="err">Could not load the categories — {error.message}</div> : null}
      {formError ? <div className="err">{formError}</div> : null}
      {note ? <div className="crd" style={{ padding: '11px 16px', marginBottom: 14, fontSize: 13 }}>{note}</div> : null}

      <div className="crd" style={{ padding: '20px 22px' }}>
        <div style={{ display: 'flex', gap: 9, marginBottom: 16, flexWrap: 'wrap' }}>
          <input className="inp" style={{ maxWidth: 280 }} placeholder="New category name"
            value={adding} onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void add(); }} />
          <button className="b bg" type="button" disabled={busy || !adding.trim()}
            onClick={() => void add()}>Add</button>
        </div>

        <div className="tblx">
          <table className="tbl">
            <thead>
              <tr><th style={{ width: 78 }}>Order</th><th>Category</th><th>Articles</th><th>Published</th><th /></tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <SkeletonRows cols={5} />
              ) : rows.map((c, i) => (
                <tr key={c.id}>
                  <td>
                    <button className="b bo" type="button" disabled={busy || i === 0}
                      style={{ padding: '3px 8px', fontSize: 12, lineHeight: 1.2 }}
                      aria-label={`Move ${c.name} up`} onClick={() => void move(c, -1)}>↑</button>
                    {' '}
                    <button className="b bo" type="button" disabled={busy || i === rows.length - 1}
                      style={{ padding: '3px 8px', fontSize: 12, lineHeight: 1.2 }}
                      aria-label={`Move ${c.name} down`} onClick={() => void move(c, 1)}>↓</button>
                  </td>
                  <td style={{ fontWeight: 700 }}>{c.name}</td>
                  <td className="muted">{c.article_count}</td>
                  <td className="muted">{c.published_count}</td>
                  <td className="right">
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }} type="button"
                      onClick={() => { setEditing(c); setDraft(c.name); }}>Rename</button>
                    {' '}
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5, color: 'var(--down)',
                      borderColor: 'rgba(240,100,91,.4)' }} type="button"
                      onClick={() => { setRemoving(c); setMoveTo(''); }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="hint" style={{ marginTop: 14 }}>
          Renaming moves every article in the category with it, so nothing is lost — but the
          category’s address on the site changes, and any link somebody saved to the old filter
          stops matching. The menu item in <strong>Header &amp; footer</strong> is separate: renaming
          here does not rename that.
        </p>
      </div>

      {editing ? (
        <Modal title={`Rename ${editing.name}`} onClose={() => setEditing(null)}
          footer={<>
            <button className="b bo" type="button" onClick={() => setEditing(null)}>Cancel</button>
            <button className="b bg" type="button" disabled={busy} onClick={() => void save()}>Rename</button>
          </>}>
          <input className="inp" value={draft} autoFocus onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void save(); }} />
          <p className="hint" style={{ marginTop: 10 }}>
            {editing.article_count
              ? `${editing.article_count} article${editing.article_count === 1 ? '' : 's'} will move with it.`
              : 'Nothing is filed under it yet.'}
          </p>
        </Modal>
      ) : null}

      {removing ? (
        <Modal title={`Delete ${removing.name}?`} onClose={() => setRemoving(null)}
          footer={<>
            <button className="b bo" type="button" onClick={() => setRemoving(null)}>Cancel</button>
            <button className="b bg" type="button" disabled={busy || (!!removing.article_count && !moveTo)}
              style={{ background: 'var(--down)', color: '#fff' }}
              onClick={() => void drop()}>Delete</button>
          </>}>
          {removing.article_count ? (
            <>
              <p style={{ fontSize: 13.5, lineHeight: 1.6 }}>
                {removing.article_count} article{removing.article_count === 1 ? ' is' : 's are'} filed
                under this. Choose where they go — they are not deleted with it.
              </p>
              <select className="inp" style={{ marginTop: 12 }} value={moveTo}
                onChange={(e) => setMoveTo(e.target.value)}>
                <option value="">Move them to…</option>
                {rows.filter((c) => c.id !== removing.id)
                  .map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </>
          ) : (
            <p style={{ fontSize: 13.5, lineHeight: 1.6 }}>Nothing is filed under it. This cannot be undone.</p>
          )}
        </Modal>
      ) : null}
    </div>
  );
}
